import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Shield, Gauge, Crown, Telescope, Infinity, Target, BookOpen } from "lucide-react";
import { VALUES, VISION } from "./data";

const ICON_MAP = {
  sparkles: Sparkles,
  shield: Shield,
  gauge: Gauge,
  crown: Crown,
  telescope: Telescope,
  infinity: Infinity,
  target: Target,
  book: BookOpen,
};

const ValuesVision = () => {
  return (
    <section id="vision" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">09 / Compass</div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Values, <span className="italic">vision</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              A short list of principles. A long-arc roadmap. The destination is less important than the direction — and the company you keep along the way.
            </p>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-28">
          {VALUES.map((v, i) => {
            const Icon = ICON_MAP[v.icon] || Sparkles;
            return (
              <motion.div
                key={v.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.03 }}
                className="group relative rounded-[18px] border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl p-6 hover:border-[#e6c769]/40 transition-all"
              >
                <Icon className="w-6 h-6 text-[#e6c769]" />
                <div className="mt-5 text-[20px] text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {v.name}
                </div>
                <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">{String(i + 1).padStart(2, "0")}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Vision roadmap */}
        <div className="relative">
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono mb-6">The next 25 years</div>
          <h3 className="text-[40px] md:text-[64px] leading-[0.95] tracking-tight text-white max-w-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
            A roadmap, written in <span className="italic">decades</span>.
          </h3>

          <div className="mt-14 relative">
            <div className="absolute left-0 right-0 top-[44px] h-px bg-gradient-to-r from-transparent via-[#e6c769]/40 to-transparent hidden md:block" />
            <div className="grid md:grid-cols-5 gap-6">
              {VISION.map((v, i) => (
                <motion.div
                  key={v.phase}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="relative"
                >
                  <div className="flex md:flex-col items-center md:items-start gap-4 md:gap-0">
                    <div className="relative shrink-0">
                      <div className="w-[90px] h-[90px] rounded-full bg-gradient-to-br from-[#e6c769]/20 to-[#d4af37]/10 border border-[#e6c769]/40 grid place-items-center backdrop-blur-xl">
                        <span className="text-[#e6c769] font-extrabold text-[22px]" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {v.phase}
                        </span>
                      </div>
                    </div>
                    <div className="md:mt-6">
                      <div className="text-[22px] text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {v.title}
                      </div>
                      <p className="mt-2 text-[13px] text-white/55 leading-relaxed">{v.body}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesVision;
